<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    :title="clockDirty.id ? 'Edit Music Clock' : 'Add New Music Clock'"
  >
    <v-card-text class="pa-6">
      <validation-observer ref="validation">
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-autocomplete
            v-model="clockDirty.playlists"
            :items="playlists.data"
            :loading="playlists.loading"
            item-value="id"
            item-text="title"
            outlined
            label="Play playlists"
            multiple
            chips
            clearable
            small-chips
            deletable-chips
            :error-messages="errors"
            return-object
          >
            <template v-slot:item="{ item: playlist }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ playlist.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ playlist.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </validation-provider>

        <div class="mb-2 font-weight-bold">
          On weekdays
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-btn-toggle
            v-model="clockDirty.days"
            multiple
            dense
            class="row no-gutters mb-2"
            color="primary"
            style="width: 100%"
          >
            <v-btn
              v-for="day in days"
              :key="day.day"
              :value="day.day"
              large
              class="col caption text-none"
            >
              {{ day.name }}
            </v-btn>
          </v-btn-toggle>
          <div class="v-text-field__details mb-2">
            <div class="v-messages theme--light error--text">
              <div class="v-messages__wrapper">
                {{ errors[0] }}
              </div>
            </div>
          </div>
        </validation-provider>

        <div class="font-weight-bold mb-2">
          At time in day
        </div>
        <v-row no-gutters>
          <v-col class="pr-2">
            <validation-provider
              v-slot="{ errors }"
              rules="required|time"
            >
              <v-text-field
                v-model="clockDirty.start_time_at"
                v-mask="'##:##'"
                type="tel"
                outlined
                clearable
                label="From"
                placeholder="00:00"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col class="pl-2">
            <validation-provider
              v-slot="{ errors }"
              rules="required|time"
            >
              <v-text-field
                v-model="clockDirty.end_time_at"
                v-mask="'##:##'"
                type="tel"
                outlined
                clearable
                label="To"
                placeholder="23:59"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>

        <v-row align="center">
          <v-col class="font-weight-bold">
            Playback Type
          </v-col>
          <v-col cols="6">
            <v-btn-toggle
              v-model="clockDirty.playback_type"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="PLAYBACK_TYPE_DEFAULT"
                large
                class="col text-none"
              >
                Regular
              </v-btn>
              <v-btn
                :value="PLAYBACK_TYPE_AFTER_SONGS"
                large
                class="col text-none"
              >
                Powerplay
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="clockDirty.playback_type === PLAYBACK_TYPE_AFTER_SONGS">
          <div class="font-weight-bold mb-2 mt-4">
            Powerplay frequency
          </div>
          <v-row align="center">
            <v-col cols="auto">
              Play
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required|numeric"
              >
                <v-text-field
                  v-model="clockDirty.playback_content_count"
                  outlined
                  type="number"
                  dense
                  hide-details
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col cols="auto">
              powerplay songs <strong>after</strong>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required|numeric"
              >
                <v-text-field
                  v-model="clockDirty.playback_every_songs"
                  outlined
                  type="number"
                  dense
                  hide-details
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col cols="auto">
              regular songs
            </v-col>
          </v-row>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="clockDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deleteClock()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import ClockModel from '@/models/ClockModel'
import Playlists from '@/services/Playlists'
import CardDialog from '@/components/helpers/CardDialog'
import Radios from '@/services/Radios'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

export default {
  name: 'EditMusicClockDialog',
  components: { ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    clock: {
      type: Object,
      default: () => cloneDeep(ClockModel.emptyModel)
    },
    radio: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      clockDirty: cloneDeep(this.clock),
      playlists: {
        loading: false,
        data: []
      },
      submitting: false,
      deleting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    visible(newValue) {
      if (!newValue) return

      this.clockDirty = cloneDeep(this.clock)
      if (this.$refs.validation) this.$refs.validation.reset()
    }
  },
  created() {
    this.days = ClockModel.days
    this.PLAYBACK_TYPE_DEFAULT = ClockModel.PLAYBACK_TYPE_DEFAULT
    this.PLAYBACK_TYPE_AFTER_SONGS = ClockModel.PLAYBACK_TYPE_AFTER_SONGS

    this.fetchPlaylists()
  },
  methods: {
    async fetchPlaylists() {
      try {
        this.playlists.loading = true

        this.playlists.data = await Playlists.fetch({
          content_type: ClockModel.CONTENT_TYPE_MUSIC,
          include_children: true
        })
      } catch (e) {

      } finally {
        this.playlists.loading = false
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      this.submitting = true

      try {
        const data = {
          content_type: ClockModel.CONTENT_TYPE_MUSIC,
          playlists: this.clockDirty.playlists.map(p => p.id),
          start_time_at: this.$dayjs(this.clockDirty.start_time_at, 'HH:mm').format('HH:mm:ss'),
          end_time_at: this.$dayjs(this.clockDirty.end_time_at, 'HH:mm').format('HH:mm:ss'),
          days: this.clockDirty.days,
          playback_type: this.clockDirty.playback_type
        }

        if (data.playback_type === ClockModel.PLAYBACK_TYPE_AFTER_SONGS) {
          data.playback_every_songs = this.clockDirty.playback_every_songs
          data.playback_content_count = this.clockDirty.playback_content_count
        }

        if (this.clockDirty.id) {
          const result = await Radios.updateClock(this.radio.id, this.clockDirty.id, data)

          this.$emit('clock:updated', result)

          this.$store.dispatch('alert', {
            text: 'Clock updated',
            color: 'success'
          })
        } else {
          const result = await Radios.storeClock(this.radio.id, data)

          this.$emit('clock:added', result)

          this.$store.dispatch('alert', {
            text: 'New clock successfully added',
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting clock',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteClock() {
      this.deleting = true

      try {
        const result = await Radios.deleteClock(this.radio.id, this.clockDirty.id)

        this.$store.dispatch('alert', {
          text: 'Clock deleted',
          color: 'success'
        })

        this.modalVisible = false
        this.$emit('clock:deleted', result)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>

</style>
